import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  FileText, Image, Film, CheckCircle, XCircle, Clock, Send,
  ChevronDown, ChevronRight, MessageSquare, Eye, Palette,
} from 'lucide-react'
import { apiClient } from '@/services/api'
import { cn } from '@/lib/utils'

interface MyContent {
  id: string
  title: string
  status: string
  type?: string
  scheduledAt?: string
  updatedAt: string
  client?: { id: string; name: string }
  editorialPlan?: { id: string; title: string; project?: { id: string; name: string } }
  socialFormat?: { id: string; name: string; type: string }
  creator?: { id: string; firstName: string; lastName: string }
  assignedTo?: { id: string; firstName: string; lastName: string }
  contentComments?: {
    id: string
    text: string
    createdAt: string
    user?: { firstName: string; lastName: string }
  }[]
}

type StatusTab = 'all' | 'CHANGES_REQUESTED' | 'IN_REVIEW' | 'APPROVED' | 'IN_PRODUCTION' | 'TODO'

const STATUS_CONFIG: Record<string, {
  label: string
  color: string
  bg: string
  border: string
  icon: typeof FileText
}> = {
  CHANGES_REQUESTED: {
    label: 'Da rifare',
    color: 'text-danger-600',
    bg: 'bg-danger-50',
    border: 'border-l-danger-500',
    icon: XCircle,
  },
  IN_REVIEW: {
    label: 'In revisione',
    color: 'text-primary-600',
    bg: 'bg-primary-50',
    border: 'border-l-primary-500',
    icon: Eye,
  },
  APPROVED: {
    label: 'Approvato',
    color: 'text-success-700',
    bg: 'bg-success-50',
    border: 'border-l-success-500',
    icon: CheckCircle,
  },
  IN_PRODUCTION: {
    label: 'In lavorazione',
    color: 'text-slate-600',
    bg: 'bg-slate-50',
    border: 'border-l-slate-400',
    icon: Palette,
  },
  TODO: {
    label: 'Da iniziare',
    color: 'text-slate-500',
    bg: 'bg-slate-50',
    border: 'border-l-slate-300',
    icon: Clock,
  },
  READY: {
    label: 'Pronto',
    color: 'text-success-700',
    bg: 'bg-success-50',
    border: 'border-l-success-500',
    icon: Send,
  },
  SCHEDULED: {
    label: 'Programmato',
    color: 'text-primary-700',
    bg: 'bg-primary-50',
    border: 'border-l-primary-500',
    icon: Clock,
  },
}

const TABS: { value: StatusTab; label: string }[] = [
  { value: 'all', label: 'Tutti' },
  { value: 'CHANGES_REQUESTED', label: 'Da rifare' },
  { value: 'IN_REVIEW', label: 'In revisione' },
  { value: 'APPROVED', label: 'Approvati' },
  { value: 'IN_PRODUCTION', label: 'In lavorazione' },
  { value: 'TODO', label: 'Da iniziare' },
]

function ContentTypeIcon({ type }: { type?: string | undefined }) {
  if (type === 'VIDEO' || type === 'REEL') return <Film className="w-3.5 h-3.5" />
  if (type === 'IMAGE' || type === 'CAROUSEL') return <Image className="w-3.5 h-3.5" />
  return <FileText className="w-3.5 h-3.5" />
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const min = Math.floor(diff / 60000)
  const hr = Math.floor(min / 60)
  const day = Math.floor(hr / 24)
  if (min < 60) return `${min} min fa`
  if (hr < 24) return `${hr}h fa`
  if (day === 1) return 'ieri'
  if (day < 7) return `${day}g fa`
  return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
}

export function MyContents() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<StatusTab>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: contents, isLoading } = useQuery<MyContent[]>({
    queryKey: ['ped', 'my-contents'],
    queryFn: async () => {
      const res = await apiClient.get<any>('/ped/my-contents')
      return (res as any)?.data ?? res ?? []
    },
    refetchInterval: 30_000,
  })

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-5 bg-slate-200 rounded w-40" />
          <div className="h-8 bg-slate-100 rounded w-full" />
          <div className="h-16 bg-slate-100 rounded" />
          <div className="h-16 bg-slate-100 rounded" />
        </div>
      </div>
    )
  }

  const allContents = contents ?? []

  // Counts per status
  const counts: Record<string, number> = {}
  for (const c of allContents) {
    counts[c.status] = (counts[c.status] ?? 0) + 1
  }

  const filtered = activeTab === 'all'
    ? allContents
    : allContents.filter(c => c.status === activeTab)

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-card">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">I Miei Contenuti</h3>
            <p className="text-xs text-slate-500 mt-0.5">Stato dei contenuti che hai creato o a cui sei assegnato</p>
          </div>
          <button
            onClick={() => navigate('/content-review')}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            Vedi tutti
          </button>
        </div>
      </div>

      {/* Status tabs */}
      <div className="px-4 py-2 border-b border-slate-200 flex gap-1 overflow-x-auto">
        {TABS.map(tab => {
          const count = tab.value === 'all' ? allContents.length : (counts[tab.value] ?? 0)
          if (tab.value !== 'all' && count === 0) return null
          return (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                activeTab === tab.value
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
              )}
            >
              {tab.label}
              <span className={cn(
                'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold tabular-nums',
                activeTab === tab.value ? 'bg-primary-600 text-white' : 'bg-slate-200 text-slate-600'
              )}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Content list */}
      <div className="divide-y divide-slate-200 max-h-[400px] overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-8 h-8 text-success-500 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              {activeTab === 'all' ? 'Nessun contenuto attivo' : 'Nessun contenuto in questo stato'}
            </p>
          </div>
        ) : (
          filtered.map(content => {
            const cfg = (STATUS_CONFIG[content.status] ?? STATUS_CONFIG.TODO) as NonNullable<typeof STATUS_CONFIG[string]>
            const StatusIcon = cfg.icon
            const isExpanded = expandedId === content.id
            const lastComment = content.contentComments?.[0]

            return (
              <div key={content.id} className={cn('border-l-2', cfg.border)}>
                <div
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/content-review?projectId=${content.editorialPlan?.project?.id ?? ''}`)}
                >
                  {/* Type icon */}
                  <div className={cn('w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0', cfg.bg, cfg.color)}>
                    <ContentTypeIcon type={content.type ?? undefined} />
                  </div>

                  {/* Title + meta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-900 truncate">{content.title}</p>
                      {content.socialFormat && (
                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-primary-50 text-primary-600 flex-shrink-0">
                          {content.socialFormat.name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {content.client && (
                        <span className="text-xs text-slate-400 truncate">{content.client.name}</span>
                      )}
                      {content.editorialPlan?.project && (
                        <>
                          <span className="text-xs text-slate-300">·</span>
                          <span className="text-xs text-primary-600 truncate">{content.editorialPlan.project.name}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Status badge */}
                  <div className={cn('flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0', cfg.bg, cfg.color)}>
                    <StatusIcon className="w-3 h-3" />
                    {cfg.label}
                  </div>

                  {/* Time */}
                  <span className="text-[10px] text-slate-400 tabular-nums flex-shrink-0 w-14 text-right">
                    {timeAgo(content.updatedAt)}
                  </span>

                  {/* Expand for comment */}
                  {content.status === 'CHANGES_REQUESTED' && lastComment && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setExpandedId(isExpanded ? null : content.id)
                      }}
                      className="p-1 text-slate-400 hover:text-slate-600 flex-shrink-0"
                    >
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>

                {/* Expanded: change request comment */}
                {isExpanded && lastComment && (
                  <div className="px-4 pb-3 pl-14">
                    <div className="bg-danger-50 border border-danger-100 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-1.5 mb-1">
                        <MessageSquare className="w-3 h-3 text-danger-500" />
                        <span className="text-[10px] font-semibold text-danger-600">
                          {lastComment.user ? `${lastComment.user.firstName} ${lastComment.user.lastName}` : 'Reviewer'}
                        </span>
                        <span className="text-[10px] text-danger-500 tabular-nums">
                          {timeAgo(lastComment.createdAt)}
                        </span>
                      </div>
                      <p className="text-xs text-danger-600 leading-relaxed">{lastComment.text}</p>
                    </div>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
